import { useFlow } from '@edreamsodigeo/frontend-core';
import {
  useAddContactDetailsAction,
  useCreateProductAction,
  useHomePageAction,
} from '@/components/HomePage/useAction';
import { PAYMENT, usePriceFreezeRouter } from '@/hooks/usePriceFreezeRouter';
import {
  PF_AUTO_RENEWAL,
  SB_ID,
  usePriceFreezeSession,
} from '@/hooks/usePriceFreezeSession';
import { useFlightSelection } from '@/hooks/useFlightSelection';
import { goToPFOffers, goToPFPayment } from '@/public/priceFreezeRouterUtils';
import { useRenewalAction } from '@/hooks/useRenewalAction';

export const useActionsToOffersPage = ({ onContinueSuccess }) => {
  // when the user clicks CONTINUE:
  // - create the SB
  // - add the contact details to the SB
  // - execute onContinueSuccess callback
  const navigationData = { formData: {} };

  const {
    loading: cdLoading,
    error: cdError,
    addContactDetails,
  } = useAddContactDetailsAction({
    onAddSuccess: () => onContinueSuccess(),
  });

  const {
    loading: sbLoading,
    error: sbError,
    onContinue: createSb,
  } = useHomePageAction({
    onContinue: (shoppingBasketId) => {
      addContactDetails(navigationData.formData, shoppingBasketId);
    },
  });

  const onContinueClick = (formData) => {
    navigationData.formData = formData;
    createSb();
  };

  return {
    loading: sbLoading || cdLoading,
    error: sbError || cdError,
    onContinueClick,
  };
};

export const useActionsToPaymentPage = ({ onContinueSuccess }) => {
  // when the user clicks CONTINUE:
  // - create the SB
  // - create the PF product
  // - execute onContinueSuccess callback
  const navigationData = { formData: {} };

  const {
    loading: pfLoading,
    error: pfError,
    createProduct,
  } = useCreateProductAction({
    onCreateSuccess: () => onContinueSuccess(),
  });

  const {
    loading: sbLoading,
    error: sbError,
    onContinue: createSb,
  } = useHomePageAction({
    onContinue: (sbId) => {
      createProduct(navigationData.formData, sbId);
    },
  });

  const onContinueClick = (formData) => {
    navigationData.formData = formData;
    createSb();
  };

  return {
    loading: sbLoading || pfLoading,
    error: sbError || pfError,
    onContinueClick,
  };
};

export const useActionsToPaymentPageWithAutorenewal = ({
  onContinueSuccess,
}) => {
  // when the user clicks CONTINUE:
  // - create the SB
  // - create the PF product
  // - execute onContinueSuccess callback
  const navigationData = { formData: {} };
  const [, setSessionItem] = usePriceFreezeSession();

  const { activateRenewal, loading: renLoading } = useRenewalAction({
    onSuccess: () => {
      setSessionItem(PF_AUTO_RENEWAL, 'true');
      onContinueSuccess();
    },
    onError: () => {
      onContinueSuccess();
    },
  });

  const {
    loading: pfLoading,
    error: pfError,
    createProduct,
  } = useCreateProductAction({
    onCreateSuccess: () => activateRenewal(),
    onError: () => {
      setSessionItem(PF_AUTO_RENEWAL, undefined);
    },
  });

  const {
    loading: sbLoading,
    error: sbError,
    onContinue: createSb,
  } = useHomePageAction({
    onContinue: (sbId) => {
      createProduct(navigationData.formData, sbId);
    },
    onError: () => {
      setSessionItem(PF_AUTO_RENEWAL, undefined);
    },
  });

  const onContinueClick = (formData) => {
    navigationData.formData = formData;
    createSb();
  };

  return {
    loading: sbLoading || pfLoading || renLoading,
    error: sbError || pfError,
    onContinueClick,
  };
};

export const useOnContinueSuccess = ({
  searchId,
  fareItineraryKey,
  segmentKeys,
}) => {
  const flow = useFlow();
  const router = usePriceFreezeRouter();
  const [getSessionItem] = usePriceFreezeSession();
  const flightSelectionProps = useFlightSelection();

  if (flow === 'mobile') {
    return (page) => router.navigateTo(page);
  }

  return (page) => {
    const shoppingBasketId = getSessionItem(SB_ID);
    const goToMethod = page === PAYMENT ? goToPFPayment : goToPFOffers;

    if (searchId && fareItineraryKey && segmentKeys) {
      goToMethod(searchId, fareItineraryKey, segmentKeys, shoppingBasketId);
    } else {
      goToMethod(
        flightSelectionProps?.searchId,
        flightSelectionProps?.fareItineraryKey,
        flightSelectionProps?.segmentKeys,
        shoppingBasketId
      );
    }
  };
};
